import axios from 'axios';

const API_URL = 'https://api.thunderbirdsmoke.net';

// Helper to add Authorization header
export const authHeader = () => {
  const token = localStorage.getItem('authToken'); // Retrieve the token from localStorage
  if (!token) {
    throw new Error('No auth token found');
  }
  return { Authorization: `Bearer ${token}` };
};


export const uploadImage = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append("image", file);

  try {
    const response = await axios.post(`${API_URL}/products/upload`, formData, {
      headers: {
        ...authHeader(),
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data.imageUrl;
  } catch (err) {
    console.error("Upload error:", err);
    throw err;
  }
};





/**
 * Product Management APIs
 */

// Fetch all products (with variants and inventory)
export const fetchProducts = async () => {
  const response = await axios.get(`${API_URL}/products`, {
    headers: authHeader(),
  });
  return response.data.map((product: any) => ({
    ...product,
    variants: product.variants || [], // Ensure variants is always an array
  }));
};


// Add a new product
// Update the addProduct and updateProduct types to match backend expectations
export const addProduct = async (productData: {
  name: string;
  sku: string;
  description?: string;
  price: number;
  supplier_price: number;
  image_url?: string;
  category_id: number | null;
  variants: {
    sku: string;
    price: number;
    image_url?: string;
    attributes: { name: string; values: string[] }[];
locations: {
  name: string;
  stocks: {
    stock_type: 'in_store' | 'online';
    stock: number;
  }[];
}[];
  }[];
}) => {
  const response = await axios.post(`${API_URL}/products`, productData, {
    headers: authHeader(),
  });
  return response.data;
};

export const updateProduct = async (
  id: number,
  productData: {
    name: string;
    sku: string;
    description?: string;
    price: number;
    supplier_price: number;
    image_url?: string;
    category_id: number | null;
    variants: {
      sku: string;
      price: number;
      image_url?: string;
      attributes: { name: string; values: string[] }[];
locations: {
  name: string;
  stocks: {
    stock_type: 'in_store' | 'online';
    stock: number;
  }[];
}[];
    }[];
  }
) => {
  const response = await axios.put(`${API_URL}/products/${id}`, productData, {
    headers: authHeader(),
  });
  return response.data;
};


// Delete a product
export const deleteProduct = async (id: number) => {
  const response = await axios.delete(`${API_URL}/products/${id}`, {
    headers: authHeader(),
  });
  return response.data;
};

// Fetch a single product by ID (with variants and inventory)
export const fetchProductById = async (id: number) => {
  const response = await axios.get(`${API_URL}/products/${id}`, {
    headers: authHeader(),
  });
  const product = response.data;
  product.variants = product.variants || []; // Ensure variants is always an array
  return product;
};

/**
 * User Management APIs
 */

// Fetch all users
// Update fetchUsers to handle direct array response
export const fetchUsers = async () => {
  try {
    const response = await axios.get(`${API_URL}/users`, {
      headers: authHeader(),
    });
    return response.data; // Direct array of users
  } catch (err) {
    console.error('Error fetching users:', err);
    throw err;
  }
};

// Add a new user
export const addUser = async (userData: {
  email: string;
  password: string;
  role: string;
  name?: string;
  phone?: string;
}) => {
  const response = await axios.post(`${API_URL}/users`, userData, {
    headers: authHeader(),
  });
  return response.data;
};

// Update a user
export const updateUser = async (
  id: number,
  userData: {
    email?: string;
    password?: string;
    role?: string;
    name?: string;
    phone?: string;
  }
) => {
  const response = await axios.put(`${API_URL}/users/${id}`, userData, {
    headers: authHeader(),
  });
  return response.data;
};

// Delete a user
export const deleteUser = async (id: number) => {
  const response = await axios.delete(`${API_URL}/users/${id}`, {
    headers: authHeader(),
  });
  return response.data;
};

// Fetch user by ID
export const fetchUserById = async (id: number) => {
  const response = await axios.get(`${API_URL}/users/${id}`, {
    headers: authHeader(),
  });
  return response.data;
};

/**
 * Location Management APIs
 */

// Fetch all locations (for stock management)
export const fetchLocations = async () => {
  const response = await axios.get(`${API_URL}/locations`, {
    headers: authHeader(),
  });
  return response.data; // Ensure this returns `{ locations: [...] }` from the backend
};

// Update a location
export const updateLocation = async (
  id: number,
  locationData: {
    name?: string;
    city?: string;
    province?: string;
    adminEmail?: string;
    adminPassword?: string;
    adminName?: string;
    adminPhone?: string;
  }
) => {
  const response = await axios.put(`${API_URL}/locations/${id}`, locationData, {
    headers: authHeader(),
  });
  return response.data;
};

// Delete a location
export const deleteLocation = async (id: number) => {
  const response = await axios.delete(`${API_URL}/locations/${id}`, {
    headers: authHeader(),
  });
  return response.data;
};

// Fetch location by ID
export const fetchLocationById = async (id: number) => {
  const response = await axios.get(`${API_URL}/locations/${id}`, {
    headers: authHeader(),
  });
  return response.data;
};

// Add a new location
export const addLocation = async (locationData: {
  name: string;
  city: string;
  province: string;
  adminName: string;
  adminEmail: string;
  adminPhone?: string;
  adminPassword: string; // Explicitly required for new locations
}) => {
  const response = await axios.post(`${API_URL}/locations`, locationData, {
    headers: authHeader(),
  });
  return response.data;
};

/**
 * Category Management APIs
 */

// Fetch all categories

export const fetchCategories = async () => {
  const response = await axios.get(`${API_URL}/categories`, { headers: authHeader() });
  return response.data;
};

export const addCategory = async (categoryName: string) => {
  const response = await axios.post(
    `${API_URL}/categories`,
    { name: categoryName },
    { headers: authHeader() }
  );
  return response.data;
};

export const fetchUserDetails = async (id: number, params: any = {}) => {
  const query = new URLSearchParams(params).toString();
  const response = await axios.get(
    `${API_URL}/users/${id}/details${query ? `?${query}` : ''}`,
    { headers: authHeader() }
  );
  return response.data;
};
